import { useState } from 'react';
import { PenTool, FileText, Paperclip, PhoneCall, Plus, X } from 'lucide-react';
import type { OrderState } from '../../pages/CreateSalesOrderPage';

interface Props {
  state: OrderState;
  updateState: (updater: (prev: OrderState) => OrderState) => void;
}

export function TermsAndAttachments({ state, updateState }: Props) {
  const [activeField, setActiveField] = useState<string | null>(null);

  const handleTextChange = (field: keyof OrderState['terms'], value: string) => {
    updateState(s => ({
      ...s,
      terms: { ...s.terms, [field]: value }
    }));
  };

  const renderField = (fieldId: keyof OrderState['terms'], label: string, icon: any, placeholder: string) => {
    if (activeField === fieldId || state.terms[fieldId]) {
      return (
        <div className="w-full rounded-card-sm border border-border bg-card p-3 shadow-xs">
          <div className="mb-2 flex items-center justify-between">
            <label className="text-label-sm uppercase tracking-wider text-on-surface-variant">{label}</label>
            <button
              onClick={() => {
                handleTextChange(fieldId, '');
                setActiveField(null);
              }}
              aria-label={`Remove ${label}`}
              className="rounded p-1 text-on-surface-subtle transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <textarea
            aria-label={label}
            className="w-full resize-none border-0 bg-transparent p-0 text-body-sm text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder={placeholder}
            rows={3}
            value={state.terms[fieldId] as string}
            onChange={(e) => handleTextChange(fieldId, e.target.value)}
            autoFocus={activeField === fieldId}
          />
        </div>
      );
    }
    
    return <ActionButton icon={icon} label={`Add ${label}`} onClick={() => setActiveField(fieldId)} />;
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2" aria-label="Terms, notes and attachments">
      {renderField('signature', 'Signature', PenTool, 'Type or upload signature...')}
      {renderField('termsAndConditions', 'Terms & Conditions', Plus, 'Enter terms and conditions...')}
      {renderField('notes', 'Notes', FileText, 'Add any private or public notes here...')}
      
      {/* Attachments is a special case (array) - keeping it simple for now */}
      <ActionButton icon={Paperclip} label="Add Attachments" onClick={() => alert('Attachment upload functionality would trigger here!')} />
      
      {renderField('additionalInfo', 'Additional Info', FileText, 'Enter any extra information...')}
      {renderField('contactDetails', 'Contact Details', PhoneCall, 'Enter phone numbers or emails...')}
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick }: { icon: any, label: string, onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex h-[54px] w-full items-center justify-center gap-2 rounded-card-sm border border-dashed border-outline-variant bg-card px-4 text-body-sm font-medium text-on-surface-variant transition-colors hover:border-primary hover:bg-primary-container/60 hover:text-primary"
    >
      <Icon className="h-4 w-4 text-primary" />
      {label}
    </button>
  );
}

